import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  RegistrationFormData,
  ValidationError,
  ValidationResult,
  ShareholderFormData,
  DirectorFormData,
} from '../interfaces/company.interface';
import { CompanyCategoryEnum } from '../enums/company.enum';

@Injectable({ providedIn: 'root' })
export class ValidationService {
  private messages: Record<string, string> = {
    required: 'This field is required.',
    email: 'Please enter a valid email address.',
    minlength: 'Value is too short.',
    maxlength: 'Value is too long.',
    pattern: 'Invalid format.',
    maxSize: 'File exceeds maximum allowed size.',
    upload: 'File upload failed.',
    type: 'File type not allowed.',
    min: 'Value is too small.',
    max: 'Value is too large.',
    nameNotAvailable: 'Name is not available. Try another.',
    validationFailed: 'Validation failed. Please check the information and try again.',
  };

  getErrorMessage(control: AbstractControl | null): string | null {
    if (!control || !control.errors) return null;

    // Prioritize certain errors over others
    const errorPriority = [
      'nameNotAvailable',
      'validationFailed',
      'required',
      'minlength',
      'maxlength',
      'email',
      'pattern',
      'min',
      'max',
      'maxSize',
      'upload',
      'type'
    ];

    let firstKey = errorPriority.find(key => control.errors![key]);

    if (!firstKey) {
      firstKey = Object.keys(control.errors)[0];
    }

    const errorValue = control.errors[firstKey];
    if (firstKey === 'minlength') {
      return `Minimum ${errorValue.requiredLength} characters required.`;
    }
    if (firstKey === 'maxlength') {
      return `Maximum ${errorValue.requiredLength} characters allowed.`;
    }
    if (firstKey === 'min' || firstKey === 'max') {
      return `${this.messages[firstKey]} (${errorValue.min ?? errorValue.max}).`;
    }
    return this.messages[firstKey];
  }

  validateCompleteForm(formData: RegistrationFormData): ValidationResult {
    const errors: ValidationError[] = [];

    if (formData.companyCategory !== CompanyCategoryEnum.TRUST) {
      const shareCapitalValidation = this.validateShareCapitalCalculation(
        formData.totalShares || 0,
        formData.parValue || 0,
      );
      if (!shareCapitalValidation.isValid) {
        errors.push({
          field: 'shareCapital',
          message: shareCapitalValidation.message!,
        });
      }

      const shareAllocationValidation = this.validateShareAllocation(
        formData.shareholders || [],
        Math.max(0, (formData.totalShares || 0)),
      );
      if (!shareAllocationValidation.isValid) {
        errors.push({
          field: 'shareholders',
          message: shareAllocationValidation.message!,
        });
      }

      // Validate director count
      const directorCountValidation = this.validateDirectorCount(
        formData.companyCategory!,
        formData.directors || [],
      );
      if (!directorCountValidation.isValid) {
        errors.push({
          field: 'directors',
          message: directorCountValidation.message!,
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate share capital = total shares × par value
   */
  validateShareCapitalCalculation(
    totalShares: number,
    parValue: number,
  ): { isValid: boolean; message?: string } {
    if (!totalShares || !parValue) {
      return { isValid: true }; // Skip if not all values present
    }

    const calculatedCapital = totalShares * parValue;
    const difference = Math.abs(calculatedCapital);

    if (difference > 0.01) {
      return {
        isValid: false,
        message: `Share Capital Mismatch: You entered ${calculatedCapital} USD, but ${totalShares} shares × ${parValue} USD = ${calculatedCapital} USD. Please adjust the values to match.`,
      };
    }

    return { isValid: true };
  }

  /**
   * Validate total allocated shares equals total shares
   */
  validateShareAllocation(
    shareholders: ShareholderFormData[],
    totalShares: number,
  ): { isValid: boolean; message?: string } {
    if (!shareholders || shareholders.length === 0 || !totalShares) {
      return { isValid: true };
    }

    const allocatedShares = shareholders.reduce((sum, sh) => {
      if (sh) {
        const allocations = sh;
        const shareholderTotal =
          (Number(allocations.ordinaryShares) || 0) +
          (Number(allocations.preferenceShares) || 0) +
          (Number(allocations.redeemableShares) || 0) +
          (Number(allocations.nonVotingShares) || 0);
        return sum + shareholderTotal;
      }

      return sum;
    }, 0);

    const totalSharesNum = Number(totalShares);
    const difference = Math.abs(totalSharesNum - allocatedShares);

    if (difference > 0) {
      const status = allocatedShares > totalSharesNum ? 'over-allocated' : 'under-allocated';
      return {
        isValid: false,
        message: `Share Allocation Error: You have ${status} ${difference} shares. Allocated: ${allocatedShares}, Total: ${totalSharesNum}. All ${totalSharesNum} shares must be distributed among shareholders.`,
      };
    }

    return { isValid: true };
  }

  /**
   * Validate share capital matches calculation
   */
  validateShareCapital(
    authorizedCapital: number,
    totalShares: number,
    parValue: number,
  ): { isValid: boolean; message?: string } {
    const capital = Number(authorizedCapital);
    const shares = Number(totalShares);
    const par = Number(parValue);

    if (!capital || !shares || !par) {
      return { isValid: true };
    }

    const calculatedCapital = shares * par;
    const difference = Math.abs(capital - calculatedCapital);

    // Allow small floating point differences
    if (difference > 0.01) {
      return {
        isValid: false,
        message: `Share Capital Mismatch: Authorized Capital (${capital.toFixed(
          2,
        )}) should equal Total Shares (${shares}) × Par Value (${par}) = ${calculatedCapital.toFixed(
          2,
        )}. Difference: ${difference.toFixed(2)}`,
      };
    }

    return { isValid: true };
  }

  /**
   * Validate minimum director count based on company category
   */
  validateDirectorCount(
    category: CompanyCategoryEnum,
    directors: DirectorFormData[],
  ): { isValid: boolean; message?: string } {
    const count = directors?.length || 0;

    if (category === CompanyCategoryEnum.PRIVATE && count < 1) {
      return {
        isValid: false,
        message: 'Private companies must have at least 1 director',
      };
    }

    if (category === CompanyCategoryEnum.PUBLIC && count < 2) {
      return {
        isValid: false,
        message: 'Public companies must have at least 2 directors',
      };
    }

    if (category === CompanyCategoryEnum.RECOGNISED && count < 1) {
      return {
        isValid: false,
        message: 'Foreign companies must have at least 1 director',
      };
    }

    return { isValid: true };
  }

  /**
   * Custom validator for share capital calculation
   */
  shareCapitalValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const authorizedCapital = control.get('authorizedShareCapital')?.value;
      const totalShares = control.get('totalShares')?.value;
      const parValue = control.get('parValue')?.value;

      if (!authorizedCapital || !totalShares || !parValue) {
        return null;
      }

      const calculatedCapital = totalShares * parValue;
      if (Math.abs(calculatedCapital - authorizedCapital) > 0.01) {
        return {
          shareCapitalMismatch: {
            authorized: authorizedCapital,
            calculated: calculatedCapital,
            message: `Authorized capital must equal ${calculatedCapital}`,
          },
        };
      }

      return null;
    };
  }

  /**
   * Custom validator for email format
   */
  emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(control.value)) {
        return {
          invalidEmail: {
            value: control.value,
            message: 'Please enter a valid email address',
          },
        };
      }

      return null;
    };
  }

  /**
   * Custom validator for phone number format
   */
  phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      // Support formats: +250XXXXXXXXX, 250XXXXXXXXX, 0XXXXXXXXX
      const phoneRegex = /^(\+?250|0)?[7][0-9]{8}$/;
      if (!phoneRegex.test(control.value.replace(/\s/g, ''))) {
        return {
          invalidPhone: {
            value: control.value,
            message: 'Please enter a valid phone number (e.g., +250712345678)',
          },
        };
      }

      return null;
    };
  }

  /**
   * Custom validator for file size
   */
  fileSizeValidator(maxSizeMb: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const file = control.value;
      const maxSizeBytes = maxSizeMb * 1024 * 1024;

      if (file.size && file.size > maxSizeBytes) {
        return {
          fileSize: {
            maxSize: maxSizeMb,
            actualSize: (file.size / 1024 / 1024).toFixed(2),
            message: `File size must not exceed ${maxSizeMb}MB`,
          },
        };
      }

      return null;
    };
  }

  /**
   * Custom validator for allowed file types
   */
  fileTypeValidator(allowedTypes: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const file = control.value;
      const fileExtension = file.name?.split('.').pop()?.toLowerCase();

      if (fileExtension && !allowedTypes.includes(`.${fileExtension}`)) {
        return {
          fileType: {
            allowed: allowedTypes.join(', '),
            actual: fileExtension,
            message: `File type must be one of: ${allowedTypes.join(', ')}`,
          },
        };
      }

      return null;
    };
  }

  /**
   * Custom validator for minimum array length
   */
  minArrayLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !Array.isArray(control.value)) {
        return null;
      }

      if (control.value.length < min) {
        return {
          minArrayLength: {
            required: min,
            actual: control.value.length,
            message: `At least ${min} item${min > 1 ? 's' : ''} required`,
          },
        };
      }

      return null;
    };
  }

  /**
   * Custom validator for ID number format (Zimbabwe)
   */
  idNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      // Zimbabwe National ID format: XX-XXXXXXX-X-XX
      const idRegex = /^[0-9]{2}-[0-9]{6,7}[A-Z]-[0-9]{2}$/;

      if (!idRegex.test(control.value)) {
        return {
          invalidIdNumber: {
            value: control.value,
            message: 'Please enter a valid ID number (e.g., 63-123456A-12)',
          },
        };
      }

      return null;
    };
  }

  /**
   * Validate all shareholders have unique IDs
   */
  validateUniqueShareholderIds(shareholders: ShareholderFormData[]): boolean {
    const ids = shareholders.map((sh) => sh.identificationNumber);
    const uniqueIds = new Set(ids);
    return ids.length === uniqueIds.size;
  }

  /**
   * Validate all directors have unique IDs
   */
  validateUniqueDirectorIds(directors: DirectorFormData[]): boolean {
    const ids = directors.map((d) => d.identificationNumber);
    const uniqueIds = new Set(ids);
    return ids.length === uniqueIds.size;
  }

  /**
   * Check if person is both shareholder and director (warning, not error)
   */
  checkDualRole(shareholders: ShareholderFormData[], directors: DirectorFormData[]): string[] {
    const shareholderIds = new Set(shareholders.map((sh) => sh.identificationNumber));
    const dualRoles: string[] = [];

    if (!directors || directors.length === 0) {
      return dualRoles;
    }

    directors.forEach((director) => {
      if (shareholderIds.has(director?.identificationNumber)) {
        dualRoles.push(director?.identificationNumber || 'N/A');
      }
    });

    return dualRoles;
  }

  /**
   * Validate required fields for specific company category
   */
  getRequiredFieldsForCategory(category: CompanyCategoryEnum): string[] {
    const commonFields = [
      'nameReservationId',
      'officeType',
      'officeCountry',
      'officeZone',
      'officeDistrict',
      'officeAddress',
      'officeEmail',
      'officePhone',
      'workingHours',
      'leaseAgreementId',
    ];

    if (category === CompanyCategoryEnum.TRUST) {
      return [
        ...commonFields,
        'trustType',
        'trustCategory',
        'trustPurpose',
        'trustProperty',
        'trustEstimatedValue',
        'trustCurrency',
        'settlor',
        'trustees',
        'beneficiaries',
        'trustDeedId',
        'sourceOfFunds',
        // 'sourceOfFundsDescription',
      ];
    }

    const companyFields = [
      ...commonFields,
      'companyType',
      'applicantPosition',
      'articleOfAssociationId',
      'businessSector',
      'mainBusinessActivity',
      'authorizedShareCapital',
      'totalShares',
      'parValue',
      'shareholders',
      'directors',
    ];

    if (category === CompanyCategoryEnum.RECOGNISED) {
      return [
        ...companyFields,
        'countryOfIncorporation',
        // Zimbabwe local-company verification is disabled; do not require its TIN value.
        // 'localCompanyTinNumber',
        'localRepresentativeId',
        'certificateOfIncorporationId',
        'constitutionalDocumentId',
        'acceptanceOfDutiesId',
      ];
    }

    return companyFields;
  }
}
